import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, ChevronDown, ChevronRight, Search,
  Users, Tag, Calendar, RefreshCw, Filter, X, TrendingDown, FilterX, Download, User
} from 'lucide-react';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/DashboardMenuHeader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import FullScreenLoader from './FullScreenLoader';
import GlobalSearch from '../components/GlobalSearch';
import { calculateWorkingDays } from '../utils/timeUtils';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const filterLabels = {
  BI_PROVEEDOR: "Casas",
  BI_MARCA: "Marca",
  BI_LINEA: "Línea",
  CLI_SEG_DES: "Segmento",
  CLI_PRIN: "Cliente",
  CLI_VEN_DES: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_REG_VERD: "Región Filtro",
  V_ANULADA: "Factura Anulada",
  CLI_INACTIVO: "Cliente Inactivo",
  BI_CLASIF2: "Clasificación",
  BI_DESC1: "Producto",
  CLI_TERRITORIO: "Territorio",
  CLI_CANAL_REAL: "Canal 2",
  BI_SEGMENTO: "Portafolio",
  CLI_NOMBRE: "Cliente Detalle",
  V_FACTURA: "Factura",
  CLIENTE_ID: "Cliente ID",
  ART_ID: "SKU ID"
};

const formatNumber = (num, isUSD = false) => {
  if (!num && num !== 0) return isUSD ? '$0' : '0';
  const value = num || 0;
  if (isUSD) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const formatK = (num, isUSD = false) => {
  if (!num && num !== 0) return isUSD ? '$0' : '0';
  const value = num || 0;
  if (Math.abs(value) >= 1000) {
    const formattedValue = (value / 1000).toFixed(0);
    return isUSD ? `$${formattedValue}K` : `${formattedValue}K`;
  }
  return formatNumber(value, isUSD);
};
const formatDollar = (num) => formatNumber(num, true);
const formatRegionName = (name) => {
  if (!name) return 'N/A';
  return name.split('(')[0].trim();
};

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000';
  if (val < 90) return '#ff4d4d';
  if (val < 100) return '#ffcc00';
  return '#00ba52';
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  if (percent < 0.03) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#cbd5e1"
      fontSize="10"
      fontWeight="600"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
          ? options.find(opt => (opt.value || opt) === selectedValues[0])?.label || selectedValues[0]
          : `${selectedValues.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);

  const areAllSelected = filteredOptions.every(opt =>
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val =>
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

  const handleSingleToggle = useCallback((optValue) => {
    const newVals = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(name, newVals);
  }, [selectedValues, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input
                type="checkbox"
                checked={areAllSelected}
                onChange={handleSelectAll}
              />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const INITIAL_FILTERS = {
  BI_PROVEEDOR: [], BI_MARCA: [], BI_LINEA: [], CLI_SEG_DES: [],
  CLI_PRIN: [], CLI_VEN_DES: [], CLI_CANAL: [], CLI_REG_VERD: [],
  V_ANULADA: [], CLI_INACTIVO: [], BI_CLASIF2: [], BI_DESC1: [],
  CLI_TERRITORIO: [], CLI_CANAL_REAL: [], BI_SEGMENTO: [], CLI_NOMBRE: [],
  V_FACTURA: [], CLIENTE_ID: [], ART_ID: []
};

const DashboardActivacion = () => {
  const { salesQueryActivation, getSalesFilters, loading: apiLoading } = useQuery();
  const { profile } = useAuth();

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [metrics, setMetrics] = useState({
    v_cantidad: 0, q_cuota: 0, porcentaje: 0,
    var_v_anual: 0, var_v_mensual: 0,
    var_q_anual: 0, var_q_mensual: 0,
    var_p_anual: 0, var_p_mensual: 0,
  });
  const [regionActivationTableData, setRegionActivationTableData] = useState([]);
  const [metricType, setMetricType] = useState('BULTOS');
  const [showOnlyInactive, setShowOnlyInactive] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedSoloCliente, setExpandedSoloCliente] = useState({});
  const [expandedMarca, setExpandedMarca] = useState({});
  const [expandedSoloMarca, setExpandedSoloMarca] = useState({});
  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const [selectedDay, setSelectedDay] = useState([]);
  const currentYear = new Date().getFullYear();
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [salesConfig, setSalesConfig] = useState(null);
  const [comparisonMode, setComparisonMode] = useState('CUOTA');
  const [devMode, setDevMode] = useState('NONE');
  const [expandedMarcas, setExpandedMarcas] = useState({});
  const requestRef = useRef(0);
  const filtersInitialized = useRef(false);
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });
  const [lowCompliance, setLowCompliance] = useState(false);

  const [activationView, setActivationView] = useState('region');
  const [marcaRegionAsesor, setMarcaRegionAsesor] = useState([]);
  const [marcaRegion, setMarcaRegion] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [paretoMarcas, setParetoMarcas] = useState([]);
  const [paretoClientes, setParetoClientes] = useState([]);

  const toggleMarca = (marca) => {
    setExpandedMarcas(prev => ({ ...prev, [marca]: !prev[marca] }));
  };

  const COLORS = ['#1a2c4e', '#0068b3', '#00ba52', '#4f8cff', '#00e6b8', '#334767'];

  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);

  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLocalLoading(true);
      try {
        const config = await getSalesFilters();
        if (!config) throw new Error("Configuración de ventas no disponible.");
        setSalesConfig(config);
        setAvailableOptions(config.availableOptions || {});
        setAvailableYears(config.availableYears || [currentYear]);

        const allSegments = config.availableOptions?.CLI_SEG_DES || [];
        const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
        const prefilteredSegments = allSegments.filter(segment =>
          !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
        );

        const prefilteredFilters = {
          ...INITIAL_FILTERS,
          V_ANULADA: ['0'],
          CLI_INACTIVO: ['0'],
          CLI_SEG_DES: prefilteredSegments
        };
        setFilters(prefilteredFilters);

        const defaultYear = config.currentYear || currentYear;
        setSelectedAnios([defaultYear]);
        setSelectedMeses([nombresMeses[new Date().getMonth()]]);
        filtersInitialized.current = true;
      } catch (err) {
        console.error('Error al cargar la configuración del dashboard:', err);
      } finally {
        setLocalLoading(false);
      }
    };
    loadDashboardData();
  }, []);

  const handleRefresh = async () => {
    clearCache();
    await fetchData();
  };

  const handleResetFilters = useCallback(() => {
    const config = salesConfig;
    if (!config?.availableOptions?.CLI_SEG_DES) return;
    const allSegments = config.availableOptions.CLI_SEG_DES;
    const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
    const prefilteredSegments = allSegments.filter(segment =>
      !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
    );
    const resetFilters = {
      ...INITIAL_FILTERS,
      V_ANULADA: ['0'],
      CLI_INACTIVO: ['0'],
      CLI_SEG_DES: []
    };
    setFilters(resetFilters);
  }, [salesConfig]);

  const processResponse = (response) => {
    console.log('🔍 RAW RESPONSE:', response);

    const kpis = Array.isArray(response.kpis) ? response.kpis[0] || {} : (response.kpis || {});

    const v_actual = Number(kpis.v_actual || 0);
    const q_actual = Number(kpis.q_actual || 0);
    const v_anio_ant = Number(kpis.v_anio_ant || 0);
    const v_mes_ant = Number(kpis.v_mes_ant || 0);
    const q_anio_ant = Number(kpis.q_anio_ant || 0);
    const q_mes_ant = Number(kpis.q_mes_ant || 0);
    const p_anio_ant = Number(kpis.p_anio_ant || 0);
    const p_mes_ant = Number(kpis.p_mes_ant || 0);

    const porcentaje = q_actual > 0 ? Math.round((v_actual / q_actual) * 100 * 10) / 10 : 0;
    const var_v_anual = v_anio_ant !== 0 ? Math.round(((v_actual - v_anio_ant) / Math.abs(v_anio_ant)) * 100 * 10) / 10 : 0;
    const var_v_mensual = v_mes_ant !== 0 ? Math.round(((v_actual - v_mes_ant) / Math.abs(v_mes_ant)) * 100 * 10) / 10 : 0;
    const var_q_anual = q_anio_ant !== 0 ? Math.round(((q_actual - q_anio_ant) / Math.abs(q_anio_ant)) * 100 * 10) / 10 : 0;
    const var_q_mensual = q_mes_ant !== 0 ? Math.round(((q_actual - q_mes_ant) / Math.abs(q_mes_ant)) * 100 * 10) / 10 : 0;
    const var_p_anual = p_anio_ant !== 0 ? Math.round(((porcentaje - p_anio_ant) / Math.abs(p_anio_ant)) * 100 * 10) / 10 : 0;
    const var_p_mensual = p_mes_ant !== 0 ? Math.round(((porcentaje - p_mes_ant) / Math.abs(p_mes_ant)) * 100 * 10) / 10 : 0;

    setMetrics({
      v_cantidad: v_actual,
      q_cuota: q_actual,
      porcentaje,
      var_v_anual,
      var_v_mensual,
      var_q_anual,
      var_q_mensual,
      var_p_anual,
      var_p_mensual,
    });

    const clientActivation = response.client_activation || [];
    const regionActivation = (response.region_activation || []).map(r => {
      const regionClients = clientActivation.filter(c => c.region === r.region);
      return {
        region: r.region,
        active_qty: Number(r.active_qty || 0),
        active_sales: Number(r.active_sales || 0),
        inactive_qty: Number(r.inactive_qty || 0),
        inactive_sales: Number(r.inactive_sales || 0),
        total_clients_in_region: Number(r.total_clients_in_region || 0),
        clients: regionClients
      };
    });
    setRegionActivationTableData(regionActivation);

    setMarcaRegionAsesor(response.marca_region_asesor || []);
    setMarcaRegion(response.marca_region || []);
    setClientList(response.client_list || []);
    setParetoMarcas(response.pareto_marcas || []);
    setParetoClientes(response.pareto_clientes || []);

    console.log('✅ DATA PROCESSED:', {
      regions: regionActivation.length,
      marcaAsesor: response.marca_region_asesor?.length || 0,
      marca: response.marca_region?.length || 0,
      clientList: response.client_list?.length || 0,
      paretoMarcas: response.pareto_marcas?.length || 0,
      paretoClientes: response.pareto_clientes?.length || 0,
    });
  };

  const fetchData = useCallback(async () => {
    if (!filtersInitialized.current) return;
    const requestId = ++requestRef.current;
    setQuerying(true);
    try {
      const safeAnios = Array.isArray(selectedAnios) ? selectedAnios : [];
      const safeMeses = Array.isArray(selectedMeses) ? selectedMeses : [];
      const payload = {
        metric_type: metricType,
        comparison_mode: comparisonMode,
        dev_mode: devMode,
        page_anios: safeAnios.filter(a => a),
        page_meses: safeMeses.filter(m => m),
        page_dias: selectedDay.filter(d => d),
        filters: filters,
        breakdown_type: 'ASESOR',
        chart_year: safeAnios?.[0] || null,
        low_compliance: lowCompliance,
        user_info: profile ? { username: profile.username, cargo: profile.cargo } : null
      };
      const rawResponse = await salesQueryActivation(payload);
      if (requestId !== requestRef.current) return;
      processResponse(rawResponse);
    } catch (err) {
      if (requestId === requestRef.current) console.error("❌ Error fetchData:", err);
    } finally {
      if (requestId === requestRef.current) setQuerying(false);
    }
  }, [metricType, comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters, lowCompliance, salesQueryActivation, profile]);

  const debouncedFetch = useRef();
  useEffect(() => {
    if (!filtersInitialized.current) return;
    if (debouncedFetch.current) clearTimeout(debouncedFetch.current);
    debouncedFetch.current = setTimeout(() => { fetchData(); }, 300);
    return () => { if (debouncedFetch.current) clearTimeout(debouncedFetch.current); };
  }, [comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters, lowCompliance, metricType]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  // ⭐ SOLO CLIENTE: agrupado por main_client (CLI_PRIN) + region
  const soloClienteData = useMemo(() => {
    const allClients = regionActivationTableData.flatMap(r => r.clients || []);
    const grouped = {};
    allClients.forEach(c => {
      const mainClient = c.main_client || c.client_name;
      const key = `${mainClient}_${c.region}`;
      if (!grouped[key]) {
        grouped[key] = {
          main_client: mainClient,
          region: c.region,
          sales_current_month: 0,
          quota_current_month: 0,
          total_sales_last_5_months: 0,
        };
      }
      grouped[key].sales_current_month += Number(c.sales_current_month || 0);
      grouped[key].quota_current_month += Number(c.quota_current_month || 0);
      grouped[key].total_sales_last_5_months += Number(c.total_sales_last_5_months || 0);
    });
    return Object.values(grouped).map(g => ({
      ...g,
      status: g.sales_current_month > 0 ? 'Activo' : 'Inactivo'
    })).sort((a, b) => b.total_sales_last_5_months - a.total_sales_last_5_months);
  }, [regionActivationTableData]);

  const soloClienteRegionData = useMemo(() => {
    const regionMap = {};
    soloClienteData.forEach(c => {
      const reg = c.region;
      if (!regionMap[reg]) {
        regionMap[reg] = { region: reg, total_clients: 0, active_qty: 0, inactive_qty: 0, oportunidad: 0, clients: [] };
      }
      regionMap[reg].clients.push(c);
      regionMap[reg].total_clients++;
      if (c.status === 'Activo') {
        regionMap[reg].active_qty++;
      } else {
        regionMap[reg].inactive_qty++;
        regionMap[reg].oportunidad += c.quota_current_month || 0;
      }
    });
    return Object.values(regionMap).map(r => ({
      ...r,
      active_pct: r.total_clients > 0 ? (r.active_qty / r.total_clients) * 100 : 0
    })).sort((a, b) => b.active_qty - a.active_qty);
  }, [soloClienteData]);

  const marcaRegionData = useMemo(() => {
    const regionMap = {};
    marcaRegionAsesor.forEach(r => {
      const reg = r.region;
      if (!regionMap[reg]) {
        regionMap[reg] = { region: reg, total_clients: 0, active_qty: 0, inactive_qty: 0, oportunidad: 0, items: [] };
      }
      regionMap[reg].items.push(r);
      regionMap[reg].total_clients += Number(r.total_clients) || 0;
      regionMap[reg].active_qty += Number(r.active_qty) || 0;
      regionMap[reg].inactive_qty += Number(r.inactive_qty) || 0;
      regionMap[reg].oportunidad += Number(r.oportunidad) || 0;
    });
    return Object.values(regionMap).map(r => ({
      ...r,
      active_pct: r.total_clients > 0 ? (r.active_qty / r.total_clients) * 100 : 0
    })).sort((a, b) => b.active_qty - a.active_qty);
  }, [marcaRegionAsesor]);

  const soloMarcaRegionData = useMemo(() => {
    const regionMap = {};
    marcaRegion.forEach(r => {
      const reg = r.region;
      if (!regionMap[reg]) {
        regionMap[reg] = { region: reg, total_clients: 0, active_qty: 0, inactive_qty: 0, oportunidad: 0, items: [] };
      }
      regionMap[reg].items.push(r);
      regionMap[reg].total_clients += Number(r.total_clients) || 0;
      regionMap[reg].active_qty += Number(r.active_qty) || 0;
      regionMap[reg].inactive_qty += Number(r.inactive_qty) || 0;
      regionMap[reg].oportunidad += Number(r.oportunidad) || 0;
    });
    return Object.values(regionMap).map(r => ({
      ...r,
      active_pct: r.total_clients > 0 ? (r.active_qty / r.total_clients) * 100 : 0
    })).sort((a, b) => b.active_qty - a.active_qty);
  }, [marcaRegion]);

  // ✅ Exportación unificada (respeta el filtro Solo Inactivos)
  const handleExportCurrentView = useCallback(() => {
    let rows = [];
    let filename = 'Activacion';

    const filterInactive = (data) => {
      if (!showOnlyInactive) return data;
      return data.filter(item => (item.inactive_qty || 0) > 0);
    };

    if (activationView === 'region') {
      const filteredData = filterInactive(regionActivationTableData);
      rows = filteredData.map(item => ({
        Región: item.region,
        'Total Clientes': item.total_clients_in_region,
        'Cant. Activos': item.active_qty,
        'Cant. Inactivos': item.inactive_qty,
        '% Activos': item.total_clients_in_region > 0
          ? ((item.active_qty / item.total_clients_in_region) * 100).toFixed(1) + '%'
          : '0.0%',
        ['Oportunidad (' + (metricType === 'USD' ? '$' : 'BL') + ')']: formatNumber(item.inactive_sales, metricType === 'USD')
      }));
      filename = 'Activacion_Region';
    } else if (activationView === 'soloCliente') {
      const filteredData = filterInactive(soloClienteRegionData);
      rows = filteredData.map(item => ({
        Región: item.region,
        'Total Clientes': item.total_clients,
        'Cant. Activos': item.active_qty,
        'Cant. Inactivos': item.inactive_qty,
        '% Activos': item.active_pct.toFixed(1) + '%',
        ['Oportunidad (' + (metricType === 'USD' ? '$' : 'BL') + ')']: formatNumber(item.oportunidad, metricType === 'USD')
      }));
      filename = 'Activacion_SoloCliente';
    } else if (activationView === 'marca') {
      const filteredData = filterInactive(marcaRegionData);
      rows = filteredData.map(item => ({
        Región: item.region,
        'Total Clientes': item.total_clients,
        'Cant. Activos': item.active_qty,
        'Cant. Inactivos': item.inactive_qty,
        '% Activos': item.active_pct.toFixed(1) + '%',
        ['Oportunidad (' + (metricType === 'USD' ? '$' : 'BL') + ')']: formatNumber(item.oportunidad, metricType === 'USD')
      }));
      filename = 'Activacion_Marca';
    } else if (activationView === 'soloMarca') {
      const filteredData = filterInactive(soloMarcaRegionData);
      rows = filteredData.map(item => ({
        Región: item.region,
        'Total Clientes': item.total_clients,
        'Cant. Activos': item.active_qty,
        'Cant. Inactivos': item.inactive_qty,
        '% Activos': item.active_pct.toFixed(1) + '%',
        ['Oportunidad (' + (metricType === 'USD' ? '$' : 'BL') + ')']: formatNumber(item.oportunidad, metricType === 'USD')
      }));
      filename = 'Activacion_SoloMarca';
    } else if (activationView === 'listadoClientes') {
      const filteredClients = showOnlyInactive 
        ? clientList.filter(item => item.status === 'Inactivo')
        : clientList;
      rows = filteredClients.map(item => ({
        Cliente: item.client_name,
        Status: item.status
      }));
      filename = 'Listado_Clientes';
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}_${new Date().getTime()}.xlsx`);
  }, [activationView, regionActivationTableData, soloClienteRegionData, marcaRegionData, soloMarcaRegionData, clientList, metricType, showOnlyInactive]);

  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />
      {(apiLoading || localLoading) && !filtersInitialized.current ? (
        <FullScreenLoader message="Cargando datos del servidor..." />
      ) : null}
      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">
              {metricType === 'USD' ? 'Activacion USD' : 'Activacion Bultos'}
            </h1>
            <div className="title-underline"></div>
          </div>
          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} title="Actualizar Datos" disabled={querying}>
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>
            <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
              <Filter size={16} /><span>Filtros</span>
            </button>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${comparisonMode === 'CUOTA' ? 'active' : ''}`} onClick={() => setComparisonMode('CUOTA')}>Cuota</button>
              <button className={`switch-toggle-btn ${comparisonMode === 'FORECAST' ? 'active' : ''}`} onClick={() => setComparisonMode('FORECAST')}>Forecast</button>
            </div>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${devMode === 'NONE' ? 'active' : ''}`} onClick={() => setDevMode('NONE')}>Sin Dev.</button>
              <button className={`switch-toggle-btn ${devMode === 'DEV' ? 'active' : ''}`} onClick={() => setDevMode('DEV')}>Con Dev.</button>
              <button className={`switch-toggle-btn ${devMode === 'FIN' ? 'active' : ''}`} onClick={() => setDevMode('FIN')}>Con D.FIN</button>
              <button className={`switch-toggle-btn ${devMode === 'NETO' ? 'active' : ''}`} onClick={() => setDevMode('NETO')}>NETO</button>
            </div>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${metricType === 'BULTOS' ? 'active' : ''}`} onClick={() => setMetricType('BULTOS')}>BULTOS</button>
              <button className={`switch-toggle-btn ${metricType === 'USD' ? 'active' : ''}`} onClick={() => setMetricType('USD')}>USD</button>
            </div>
            <div className="date-filters-group">
              <MultiSelectFilter label="Mes" name="meses" icon={Calendar} options={nombresMeses} selectedValues={selectedMeses} onChange={(name, vals) => setSelectedMeses(vals)} />
              <MultiSelectFilter label="Año" name="anios" icon={Calendar} options={availableYears.map(year => ({ value: year, label: year.toString() }))} selectedValues={selectedAnios} onChange={(name, vals) => setSelectedAnios(vals)} />
            </div>
            <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
              <GlobalSearch availableOptions={availableOptions} filterLabels={filterLabels} filters={filters} setFilters={setFilters} />
            </div>
          </div>
        </header>

        {(() => {
          const timeVsSalesDiff = parseFloat(metrics.porcentaje) - parseFloat(workDayMetrics.percentage);
          return (
            <div className="working-days-bar">
              <div className="wd-item"><span>Días Hábiles:</span> <strong>{workDayMetrics.total}</strong></div>
              <div className="wd-item"><span>Transcurridos:</span> <strong>{workDayMetrics.elapsed}</strong></div>
              <div className="wd-item"><span>Avance:</span> <strong>{workDayMetrics.percentage}%</strong></div>
              <div className="wd-separator"></div>
              <div className={`wd-item variation-item ${timeVsSalesDiff >= 0 ? 'positive' : 'negative'}`}>
                {timeVsSalesDiff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <strong>{timeVsSalesDiff.toFixed(1)}%</strong>
                <span className="wd-label">(vs Avance)</span>
              </div>
            </div>
          );
        })()}

        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros Disponibles</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros"><FilterX size={16} /></button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}><X size={20} /></button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter key={key} label={filterLabels[key] || key} name={key} icon={key.includes('CLI') ? Users : Tag} options={availableOptions[key] || []} selectedValues={filters[key] || []} onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))} />
            ))}
            <div className="filter-pill-container">
              <button className={`filter-pill-button ${lowCompliance ? 'active low-compliance' : ''}`} onClick={() => setLowCompliance(!lowCompliance)} title="Filtrar por cumplimiento menor al 80%">
                <span className="pill-label">Cumplimiento &lt; 80%</span>
              </button>
            </div>
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters} title="Restablecer filtros">
              <FilterX size={12} className="pill-icon" /><span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* 🔥 Tarjetas de Pareto */}
        <div className="dashboard-indicators">
          <div className="indicator-card v-real">
            <div className="card-content">
              <span className="indicator-label">Pareto Marcas (Mes Actual)</span>
              <div className="pareto-list" style={{ marginTop: '8px' }}>
                {paretoMarcas.length === 0 ? (
                  <div style={{ fontSize: '0.7rem', color: '#666', textAlign: 'center', padding: '8px' }}>
                    Sin ventas en el mes actual
                  </div>
                ) : (
                  paretoMarcas.slice(0, 5).map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span style={{ flex: 1, fontSize: '0.7rem', color: '#cbd5e1' }}>{item.brand}</span>
                      <span style={{ fontSize: '0.7rem', color: '#fff', fontWeight: 'bold', minWidth: '45px', textAlign: 'right' }}>
                        {item.porcentaje}%
                      </span>
                      <span style={{ fontSize: '0.65rem', color: '#888', minWidth: '50px', textAlign: 'right' }}>
                        Acum. {parseFloat(item.cum_porcentaje).toFixed(1)}%
                      </span>
                    </div>
                  ))
                )}
                {paretoMarcas.length > 5 && (
                  <div style={{ fontSize: '0.6rem', color: '#666', marginTop: '4px', textAlign: 'center' }}>
                    + {paretoMarcas.length - 5} marcas adicionales...
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="indicator-card v-real">
            <div className="card-content">
              <span className="indicator-label">Pareto Clientes (Mes Actual)</span>
              <div className="pareto-list" style={{ marginTop: '8px' }}>
                {paretoClientes.length === 0 ? (
                  <div style={{ fontSize: '0.7rem', color: '#666', textAlign: 'center', padding: '8px' }}>
                    Sin ventas en el mes actual
                  </div>
                ) : (
                  paretoClientes.slice(0, 5).map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span style={{ flex: 1, fontSize: '0.7rem', color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.client_name}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: '#fff', fontWeight: 'bold', minWidth: '45px', textAlign: 'right' }}>
                        {item.porcentaje}%
                      </span>
                      <span style={{ fontSize: '0.65rem', color: '#888', minWidth: '50px', textAlign: 'right' }}>
                        Acum. {parseFloat(item.cum_porcentaje).toFixed(1)}%
                      </span>
                    </div>
                  ))
                )}
                {paretoClientes.length > 5 && (
                  <div style={{ fontSize: '0.6rem', color: '#666', marginTop: '4px', textAlign: 'center' }}>
                    + {paretoClientes.length - 5} clientes adicionales...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TABLA UNIFICADA */}
        <div className="detail-table-container2" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s', marginTop: '20px' }}>
          <div className="chart-container mini table-fixed-height" style={{ flex: 1, gridColumn: 'span 2' }}>
            <div className="table-header-container">
              <h3 className="chart-title">Resumen de Activación por Región (Mes Actual vs Histórico 5m)</h3>
              <div className="chart-header-filters" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="switch-toggle-group">
                  <button className={`switch-toggle-btn ${activationView === 'region' ? 'active' : ''}`} onClick={() => setActivationView('region')}>Región</button>
                  <button className={`switch-toggle-btn ${activationView === 'soloCliente' ? 'active' : ''}`} onClick={() => setActivationView('soloCliente')}>Solo Cliente</button>
                  <button className={`switch-toggle-btn ${activationView === 'marca' ? 'active' : ''}`} onClick={() => setActivationView('marca')}>Marca</button>
                  <button className={`switch-toggle-btn ${activationView === 'soloMarca' ? 'active' : ''}`} onClick={() => setActivationView('soloMarca')}>Solo Marca</button>
                  <button className={`switch-toggle-btn ${activationView === 'listadoClientes' ? 'active' : ''}`} onClick={() => setActivationView('listadoClientes')}>Listado Clientes</button>
                </div>
                {/* El toggle de inactivos ahora SIEMPRE visible, sin excepción */}
                <div className="switch-toggle-group">
                  <button className={`switch-toggle-btn ${!showOnlyInactive ? 'active' : ''}`} onClick={() => setShowOnlyInactive(false)}>Todos</button>
                  <button className={`switch-toggle-btn ${showOnlyInactive ? 'active' : ''}`} onClick={() => setShowOnlyInactive(true)}>Solo Inactivos</button>
                </div>
                <button className="export-table-btn" onClick={handleExportCurrentView} title="Descargar datos actuales">
                  <Download size={12} />
                </button>
              </div>
            </div>

            <div className="minimal-table-wrapper">
              {(() => {
                if (activationView === 'listadoClientes') {
                  return (
                    <div className="table-header2" style={{ gridTemplateColumns: '2fr 1fr', height: 'auto', display: 'grid', padding: '12px 10px' }}>
                      <div className="col-name">Cliente</div>
                      <div className="col-data">Status</div>
                    </div>
                  );
                }
                return (
                  <div className="table-header2" style={{ gridTemplateColumns: '1.5fr repeat(5, 1fr)', height: 'auto', display: 'grid', padding: '12px 10px' }}>
                    <div className="col-name">Región</div>
                    <div className="col-data">Total Clientes</div>
                    <div className="col-data">Cant. Activos</div>
                    <div className="col-data">Cant. Inactivos</div>
                    <div className="col-data">% Activos</div>
                    <div className="col-data">Oportunidad ({metricType === 'USD' ? '$' : 'BL'})</div>
                  </div>
                );
              })()}

              <div className="table-body2">
                {/* VISTA REGIÓN */}
                {activationView === 'region' && regionActivationTableData
                  .filter(row => !showOnlyInactive || row.inactive_qty > 0)
                  .map((row, idx) => {
                    const activePct = row.total_clients_in_region > 0 ? (row.active_qty / row.total_clients_in_region) * 100 : 0;
                    const isExpanded = expandedRegions[row.region];
                    return (
                      <React.Fragment key={idx}>
                        <div className={`table-row2 pointer ${isExpanded ? 'active-row' : ''}`} style={{ gridTemplateColumns: '1.5fr repeat(5, 1fr)' }} onClick={() => setExpandedRegions(prev => ({ ...prev, [row.region]: !isExpanded }))}>
                          <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {row.region}
                          </div>
                          <div className="col-data">{formatNumber(row.total_clients_in_region)}</div>
                          <div className="col-data">{formatNumber(row.active_qty)}</div>
                          <div className="col-data">{formatNumber(row.inactive_qty)}</div>
                          <div className="col-data bold" style={{ color: activePct >= 80 ? '#00ba52' : (activePct >= 50 ? '#ffcc00' : '#ff4d4d') }}>{activePct.toFixed(1)}%</div>
                          <div className="col-data">{formatNumber(row.inactive_sales, metricType === 'USD')}</div>
                        </div>
                        {isExpanded && (
                          <div className="region-detail-container" style={{ backgroundColor: 'rgba(255,255,255,0.02)', padding: '5px 0', maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', padding: '8px 10px', fontSize: '0.65rem', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', opacity: 0.8, position: 'sticky', top: 0, backgroundColor: '#1e1e24', zIndex: 3 }}>
                              <div style={{ textAlign: 'left', opacity: 0.7 }}>Vendedor</div>
                              <div style={{ opacity: 0.7 }}>Cliente Detalle</div>
                              <div style={{ textAlign: 'right' }}>Total</div>
                              <div style={{ textAlign: 'right' }}>Activo</div>
                              <div style={{ textAlign: 'right' }}>Status</div>
                              <div style={{ textAlign: 'right', opacity: 0.7 }}>Oportunidad ({metricType === 'USD' ? '$' : 'BL'})</div>
                            </div>
                            {row.clients
                              .filter(c => !showOnlyInactive || c.status === 'Inactivo')
                              .sort((a, b) => {
                                const sellerCompare = (a.salesperson || '').localeCompare(b.salesperson || '');
                                if (sellerCompare !== 0) return sellerCompare;
                                if (a.status !== b.status) return a.status === 'Activo' ? -1 : 1;
                                return (b.sales_current_month || 0) - (a.sales_current_month || 0);
                              })
                              .map((client, cIdx, arr) => {
                                const showSeller = cIdx === 0 || arr[cIdx - 1].salesperson !== client.salesperson;
                                return (
                                  <React.Fragment key={cIdx}>
                                    {showSeller && (
                                      <div style={{ gridColumn: '1 / -1', background: 'rgba(30, 41, 59, 0.95)', color: '#4f8cff', fontWeight: 'bold', padding: '8px 10px', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '6px', position: 'sticky', top: '29px', zIndex: 2 }}>
                                        <User size={14} /> <span>{client.salesperson}</span>
                                      </div>
                                    )}
                                    <div className="table-row-detail" style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', padding: '6px 10px', fontSize: '0.7rem', borderBottom: '1px solid rgba(255,255,255,0.03)', color: '#fff' }}>
                                      <div />
                                      <div className="truncate bold" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: client.status === 'Activo' ? '#00ba52' : '#ff4d4d', flexShrink: 0 }} />
                                        {client.client_name}
                                      </div>
                                      <div style={{ textAlign: 'right' }}>1</div>
                                      <div style={{ textAlign: 'right' }}>{client.status === 'Activo' ? 1 : 0}</div>
                                      <div style={{ textAlign: 'right', color: client.status === 'Activo' ? '#00ba52' : '#ff4d4d', fontSize: '0.65rem' }}>{client.status}</div>
                                      <div style={{ textAlign: 'right', color: '#ff4d4d', fontWeight: client.status === 'Inactivo' ? 'bold' : 'normal' }}>
                                        {client.status === 'Inactivo' ? formatNumber(client.quota_current_month, metricType === 'USD') : ''}
                                      </div>
                                    </div>
                                  </React.Fragment>
                                );
                              })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                {/* SOLO CLIENTE */}
                {activationView === 'soloCliente' && soloClienteRegionData
                  .filter(r => !showOnlyInactive || r.inactive_qty > 0)
                  .map((row, idx) => {
                    const isExpanded = expandedSoloCliente[row.region];
                    return (
                      <React.Fragment key={idx}>
                        <div className={`table-row2 pointer ${isExpanded ? 'active-row' : ''}`} style={{ gridTemplateColumns: '1.5fr repeat(5, 1fr)' }} onClick={() => setExpandedSoloCliente(prev => ({ ...prev, [row.region]: !isExpanded }))}>
                          <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {row.region}
                          </div>
                          <div className="col-data">{formatNumber(row.total_clients)}</div>
                          <div className="col-data">{formatNumber(row.active_qty)}</div>
                          <div className="col-data">{formatNumber(row.inactive_qty)}</div>
                          <div className="col-data bold" style={{ color: row.active_pct >= 80 ? '#00ba52' : (row.active_pct >= 50 ? '#ffcc00' : '#ff4d4d') }}>{row.active_pct.toFixed(1)}%</div>
                          <div className="col-data">{formatNumber(row.oportunidad, metricType === 'USD')}</div>
                        </div>
                        {isExpanded && (
                          <div className="region-detail-container" style={{ backgroundColor: 'rgba(255,255,255,0.02)', padding: '5px 0', maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', padding: '8px 10px', fontSize: '0.65rem', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', opacity: 0.8, position: 'sticky', top: 0, backgroundColor: '#1e1e24', zIndex: 3 }}>
                              <div style={{ textAlign: 'left', opacity: 0.7 }}>Vendedor</div>
                              <div style={{ opacity: 0.7 }}>Cliente Principal</div>
                              <div style={{ textAlign: 'right' }}>Total</div>
                              <div style={{ textAlign: 'right' }}>Activo</div>
                              <div style={{ textAlign: 'right' }}>Status</div>
                              <div style={{ textAlign: 'right', opacity: 0.7 }}>Oportunidad ({metricType === 'USD' ? '$' : 'BL'})</div>
                            </div>
                            {row.clients
                              .filter(c => !showOnlyInactive || c.status === 'Inactivo')
                              .sort((a, b) => (a.main_client || '').localeCompare(b.main_client || ''))
                              .map((client, cIdx) => (
                                <div key={cIdx} className="table-row-detail" style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', padding: '6px 10px', fontSize: '0.7rem', borderBottom: '1px solid rgba(255,255,255,0.03)', color: '#fff' }}>
                                  <div />
                                  <div className="truncate bold" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: client.status === 'Activo' ? '#00ba52' : '#ff4d4d', flexShrink: 0 }} />
                                    {client.main_client}
                                  </div>
                                  <div style={{ textAlign: 'right' }}>1</div>
                                  <div style={{ textAlign: 'right' }}>{client.status === 'Activo' ? 1 : 0}</div>
                                  <div style={{ textAlign: 'right', color: client.status === 'Activo' ? '#00ba52' : '#ff4d4d', fontSize: '0.65rem' }}>{client.status}</div>
                                  <div style={{ textAlign: 'right', color: '#ff4d4d', fontWeight: client.status === 'Inactivo' ? 'bold' : 'normal' }}>
                                    {client.status === 'Inactivo' ? formatNumber(client.quota_current_month, metricType === 'USD') : ''}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                {/* MARCA */}
                {activationView === 'marca' && marcaRegionData
                  .filter(r => !showOnlyInactive || r.inactive_qty > 0)
                  .map((row, idx) => {
                    const isExpanded = expandedMarca[row.region];
                    return (
                      <React.Fragment key={idx}>
                        <div className={`table-row2 pointer ${isExpanded ? 'active-row' : ''}`} style={{ gridTemplateColumns: '1.5fr repeat(5, 1fr)' }} onClick={() => setExpandedMarca(prev => ({ ...prev, [row.region]: !isExpanded }))}>
                          <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {row.region}
                          </div>
                          <div className="col-data">{formatNumber(row.total_clients)}</div>
                          <div className="col-data">{formatNumber(row.active_qty)}</div>
                          <div className="col-data">{formatNumber(row.inactive_qty)}</div>
                          <div className="col-data bold" style={{ color: row.active_pct >= 80 ? '#00ba52' : (row.active_pct >= 50 ? '#ffcc00' : '#ff4d4d') }}>{row.active_pct.toFixed(1)}%</div>
                          <div className="col-data">{formatNumber(row.oportunidad, metricType === 'USD')}</div>
                        </div>
                        {isExpanded && (
                          <div className="region-detail-container" style={{ backgroundColor: 'rgba(255,255,255,0.02)', padding: '5px 0', maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', padding: '8px 10px', fontSize: '0.65rem', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', opacity: 0.8, position: 'sticky', top: 0, backgroundColor: '#1e1e24', zIndex: 3 }}>
                              <div style={{ textAlign: 'left', opacity: 0.7 }}>Asesor</div>
                              <div style={{ opacity: 0.7 }}>Marca</div>
                              <div style={{ textAlign: 'right' }}>Total</div>
                              <div style={{ textAlign: 'right' }}>Activo</div>
                              <div style={{ textAlign: 'right' }}>Status</div>
                              <div style={{ textAlign: 'right', opacity: 0.7 }}>Oportunidad ({metricType === 'USD' ? '$' : 'BL'})</div>
                            </div>
                            {row.items
                              .filter(item => !showOnlyInactive || item.inactive_qty > 0)
                              .sort((a, b) => (a.salesperson || '').localeCompare(b.salesperson || ''))
                              .map((item, i) => {
                                const activePct = item.active_pct || 0;
                                const status = item.active_qty > 0 ? 'Activo' : 'Inactivo';
                                return (
                                  <div key={i} className="table-row-detail" style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', padding: '6px 10px', fontSize: '0.7rem', borderBottom: '1px solid rgba(255,255,255,0.03)', color: '#fff' }}>
                                    <div className="truncate" style={{ color: '#4f8cff', fontWeight: 'bold' }}>{item.salesperson}</div>
                                    <div className="truncate">{item.brand}</div>
                                    <div style={{ textAlign: 'right' }}>{formatNumber(item.total_clients)}</div>
                                    <div style={{ textAlign: 'right' }}>{formatNumber(item.active_qty)}</div>
                                    <div style={{ textAlign: 'right', color: status === 'Activo' ? '#00ba52' : '#ff4d4d', fontSize: '0.65rem' }}>{status}</div>
                                    <div style={{ textAlign: 'right', color: '#ff4d4d', fontWeight: status === 'Inactivo' ? 'bold' : 'normal' }}>
                                      {status === 'Inactivo' ? formatNumber(item.oportunidad, metricType === 'USD') : ''}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                {/* SOLO MARCA */}
                {activationView === 'soloMarca' && soloMarcaRegionData
                  .filter(r => !showOnlyInactive || r.inactive_qty > 0)
                  .map((row, idx) => {
                    const isExpanded = expandedSoloMarca[row.region];
                    return (
                      <React.Fragment key={idx}>
                        <div className={`table-row2 pointer ${isExpanded ? 'active-row' : ''}`} style={{ gridTemplateColumns: '1.5fr repeat(5, 1fr)' }} onClick={() => setExpandedSoloMarca(prev => ({ ...prev, [row.region]: !isExpanded }))}>
                          <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {row.region}
                          </div>
                          <div className="col-data">{formatNumber(row.total_clients)}</div>
                          <div className="col-data">{formatNumber(row.active_qty)}</div>
                          <div className="col-data">{formatNumber(row.inactive_qty)}</div>
                          <div className="col-data bold" style={{ color: row.active_pct >= 80 ? '#00ba52' : (row.active_pct >= 50 ? '#ffcc00' : '#ff4d4d') }}>{row.active_pct.toFixed(1)}%</div>
                          <div className="col-data">{formatNumber(row.oportunidad, metricType === 'USD')}</div>
                        </div>
                        {isExpanded && (
                          <div className="region-detail-container" style={{ backgroundColor: 'rgba(255,255,255,0.02)', padding: '5px 0', maxHeight: '400px', overflowY: 'auto', position: 'relative' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', padding: '8px 10px', fontSize: '0.65rem', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', opacity: 0.8, position: 'sticky', top: 0, backgroundColor: '#1e1e24', zIndex: 3 }}>
                              <div style={{ textAlign: 'left', opacity: 0.7 }}>Vendedor</div>
                              <div style={{ opacity: 0.7 }}>Marca</div>
                              <div style={{ textAlign: 'right' }}>Total</div>
                              <div style={{ textAlign: 'right' }}>Activo</div>
                              <div style={{ textAlign: 'right' }}>Status</div>
                              <div style={{ textAlign: 'right', opacity: 0.7 }}>Oportunidad ({metricType === 'USD' ? '$' : 'BL'})</div>
                            </div>
                            {row.items
                              .filter(item => !showOnlyInactive || item.inactive_qty > 0)
                              .sort((a, b) => (a.brand || '').localeCompare(b.brand || ''))
                              .map((item, i) => {
                                const activePct = item.active_pct || 0;
                                const status = item.active_qty > 0 ? 'Activo' : 'Inactivo';
                                return (
                                  <div key={i} className="table-row-detail" style={{ display: 'grid', gridTemplateColumns: '1.5fr repeat(5, 1fr)', padding: '6px 10px', fontSize: '0.7rem', borderBottom: '1px solid rgba(255,255,255,0.03)', color: '#fff' }}>
                                    <div />
                                    <div className="truncate">{item.brand}</div>
                                    <div style={{ textAlign: 'right' }}>{formatNumber(item.total_clients)}</div>
                                    <div style={{ textAlign: 'right' }}>{formatNumber(item.active_qty)}</div>
                                    <div style={{ textAlign: 'right', color: status === 'Activo' ? '#00ba52' : '#ff4d4d', fontSize: '0.65rem' }}>{status}</div>
                                    <div style={{ textAlign: 'right', color: '#ff4d4d', fontWeight: status === 'Inactivo' ? 'bold' : 'normal' }}>
                                      {status === 'Inactivo' ? formatNumber(item.oportunidad, metricType === 'USD') : ''}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                {/* LISTADO CLIENTES con filtro de inactivos */}
                {activationView === 'listadoClientes' && clientList
                  .filter(row => !showOnlyInactive || row.status === 'Inactivo')
                  .map((row, idx) => (
                    <div key={idx} className="table-row2" style={{ gridTemplateColumns: '2fr 1fr' }}>
                      <div className="col-name truncate bold">{row.client_name}</div>
                      <div className="col-data" style={{ color: row.status === 'Activo' ? '#00ba52' : '#ff4d4d', fontSize: '0.7rem', fontWeight: 'bold' }}>
                        {row.status}
                      </div>
                    </div>
                  ))
                }
              </div>

              {['region', 'soloCliente', 'marca', 'soloMarca'].includes(activationView) && (
                <div className="table-footer2" style={{ gridTemplateColumns: '1.5fr repeat(5, 1fr)' }}>
                  <div className="col-name">TOTALES</div>
                  {(() => {
                    const data =
                      activationView === 'region' ? regionActivationTableData :
                      activationView === 'soloCliente' ? soloClienteRegionData :
                      activationView === 'marca' ? marcaRegionData :
                      soloMarcaRegionData;
                    const totalActive = data.reduce((acc, r) => acc + (r.active_qty || 0), 0);
                    const totalInactive = data.reduce((acc, r) => acc + (r.inactive_qty || 0), 0);
                    const total = totalActive + totalInactive;
                    const totalPct = total > 0 ? (totalActive / total) * 100 : 0;
                    const totalOportunidad = data.reduce((acc, r) => acc + (r.oportunidad || r.inactive_sales || 0), 0);
                    return (
                      <>
                        <div className="col-data">{formatNumber(total)}</div>
                        <div className="col-data">{formatNumber(totalActive)}</div>
                        <div className="col-data">{formatNumber(totalInactive)}</div>
                        <div className="col-data bold" style={{ color: totalPct >= 80 ? '#00ba52' : (totalPct >= 50 ? '#ffcc00' : '#ff4d4d') }}>{totalPct.toFixed(1)}%</div>
                        <div className="col-data">{formatNumber(totalOportunidad, metricType === 'USD')}</div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        <footer className="status-footer">
          {querying ? "SINCRONIZANDO..." : `VISTA ACTUAL: ${selectedMeses.length > 0 ? selectedMeses.join(', ') : 'TODOS'} ${selectedAnios.length > 0 ? selectedAnios.join(', ') : ''}`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default DashboardActivacion;